import { useRef, useEffect, useCallback, useState } from "react";
import type { TelemetryValue } from "../types/telemetry";
import type { SamplePoint } from "../types/analysis";
import { apiFetch } from "../apiFetch";


/** Default sample interval: 1 Hz */
const SAMPLE_INTERVAL_MS = 1000;

/** Default flush interval: 5 seconds */
const FLUSH_INTERVAL_MS = 5000;

/**
 * Upper bound on buffered samples while the API is unreachable. At 1 Hz over
 * ~10 channels this is roughly an hour of data; beyond that we drop the oldest
 * rather than grow the kiosk's heap without limit.
 */
export const MAX_BUFFERED_SAMPLES = 36_000;

/**
 * Client-side telemetry sampler.
 *
 * Taps into live metrics from useTelemetry, samples at 1 Hz,
 * buffers in memory, and batch-POSTs to the server every 5 seconds.
 */
export function useTelemetrySampler(
  metrics: Map<string, TelemetryValue>,
  sessionId: number | null,
  currentLapId: number | null,
  channels: string[],
) {
  const bufferRef = useRef<SamplePoint[]>([]);
  const metricsRef = useRef(metrics);
  metricsRef.current = metrics;

  const sessionIdRef = useRef(sessionId);
  sessionIdRef.current = sessionId;

  const lapIdRef = useRef(currentLapId);
  const channelsRef = useRef(channels);
  channelsRef.current = channels;

  const [sampleCount, setSampleCount] = useState(0);

  const flush = useCallback(async () => {
    const sid = sessionIdRef.current;
    if (!sid || bufferRef.current.length === 0) return;

    const samples = bufferRef.current.splice(0, bufferRef.current.length);
    try {
      const res = await apiFetch(`/api/sessions/${sid}/samples`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ samples }),
      });
      if (!res.ok) {
        requeue(samples);
        console.warn("[sampler] Flush failed:", res.status);
      } else {
        setSampleCount(bufferRef.current.length);
      }
    } catch (err) {
      requeue(samples);
      console.warn("[sampler] Flush error:", err);
    }
  }, []);

  function requeue(samples: SamplePoint[]) {
    const merged = samples.concat(bufferRef.current);
    const overflow = merged.length - MAX_BUFFERED_SAMPLES;
    if (overflow > 0) {
      console.warn(`[sampler] Buffer full, dropping ${overflow} oldest samples`);
    }
    bufferRef.current = overflow > 0 ? merged.slice(overflow) : merged;
    setSampleCount(bufferRef.current.length);
  }

  // Sample tick — reads current metric values into buffer
  useEffect(() => {
    if (!sessionId) return;

    const interval = setInterval(() => {
      const now = Date.now();
      const m = metricsRef.current;
      const chs = channelsRef.current;
      const lid = lapIdRef.current;

      for (const ch of chs) {
        const tv = m.get(ch);
        if (tv && !isNaN(tv.value)) {
          bufferRef.current.push({
            timestampMs: now,
            channel: ch,
            value: tv.value,
            lapId: lid,
          });
        }
      }
      setSampleCount(bufferRef.current.length);
    }, SAMPLE_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [sessionId]);

  // Flush tick — sends buffered samples to server
  useEffect(() => {
    if (!sessionId) return;

    const interval = setInterval(flush, FLUSH_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [sessionId, flush]);

  // Force flush when lap changes (lap completed)
  useEffect(() => {
    lapIdRef.current = currentLapId;
    if (currentLapId !== null) {
      flush();
    }
  }, [currentLapId, flush]);

  // Force flush when session ends (sessionId becomes null)
  const prevSessionIdRef = useRef(sessionId);
  useEffect(() => {
    if (prevSessionIdRef.current !== null && sessionId === null) {
      flush();
    }
    prevSessionIdRef.current = sessionId;
  }, [sessionId, flush]);

  return { sampleCount, flush };
}
